import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { withdrawalsAPI, walletAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Bank, CurrencyDollar, CheckCircle, Clock, XCircle, PaypalLogo, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { VoiceInputButton } from '../components/VoiceInputButton';

export default function WithdrawPage() {
  const { user, refreshUser } = useAuth();

  const [balance, setBalance] = useState(0);
  const [method, setMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [cashappTag, setCashappTag] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [balRes, wdRes] = await Promise.all([
        walletAPI.getBalance(),
        withdrawalsAPI.getMy()
      ]);
      setBalance(balRes.data.balance || 0);
      setWithdrawals(wdRes.data || []);
    } catch (err) { /* silent */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) { toast.error('Enter a valid amount'); return; }
    if (parsedAmount > balance) { toast.error('Insufficient balance'); return; }

    setLoading(true);
    try {
      await withdrawalsAPI.request({
        amount: parsedAmount,
        method,
        bank_name: method === 'bank_transfer' ? bankName : null,
        account_holder: method === 'bank_transfer' ? accountHolder : null,
        routing_number: method === 'bank_transfer' ? routingNumber : null,
        account_number: method === 'bank_transfer' ? accountNumber : null,
        paypal_email: method === 'paypal' ? paypalEmail : null,
        cashapp_tag: method === 'cashapp' ? cashappTag : null,
        notes
      });
      toast.success('Withdrawal request submitted! You will be notified when it is processed.');
      setAmount('');
      setNotes('');
      refreshUser();
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const hasPending = withdrawals.some(w => w.status === 'pending');

  const statusBadge = (status) => {
    const map = {
      pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', icon: <Clock className="w-3 h-3" weight="fill" />, label: 'Pending' },
      approved: { bg: 'bg-green-500/10', text: 'text-green-400', icon: <CheckCircle className="w-3 h-3" weight="fill" />, label: 'Approved' },
      rejected: { bg: 'bg-red-500/10', text: 'text-red-400', icon: <XCircle className="w-3 h-3" weight="fill" />, label: 'Rejected' },
    };
    const s = map[status] || map.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
        {s.icon} {s.label}
      </span>
    );
  };

  const methodLabel = (m) => {
    if (m === 'bank_transfer') return 'Bank Transfer';
    if (m === 'paypal') return 'PayPal';
    if (m === 'cashapp') return 'Cash App';
    return m;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-20 border-b bg-[#0A0A0A]/90 border-[#222222] backdrop-blur-md">
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center">
          <Link to="/wallet" className="flex items-center gap-2 text-sm text-[#999999] hover:text-[#22C55E] transition" data-testid="back-to-wallet">
            <ArrowLeft className="w-4 h-4" /> Back to Wallet
          </Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#22C55E]/10 flex items-center justify-center">
            <Bank className="w-5 h-5 text-[#22C55E]" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white" data-testid="withdraw-title">Withdraw Funds</h1>
            <p className="text-sm text-[#999999]">Transfer money to your bank or payment account</p>
          </div>
        </div>

        {/* Balance Card */}
        <Card className="bg-[#111111] border-[#222222]">
          <CardContent className="p-4">
            <p className="text-xs text-[#999999] uppercase tracking-wider">Available Balance</p>
            <p className="text-3xl font-bold mt-1 text-white" data-testid="available-balance">
              ${balance.toFixed(2)}
            </p>
          </CardContent>
        </Card>

        {hasPending && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-400 flex items-center gap-2" data-testid="pending-warning">
            <Warning className="w-4 h-4 shrink-0" />
            You have a pending withdrawal. Please wait for it to be processed before requesting another.
          </div>
        )}

        {/* Withdrawal Form */}
        <Card className="bg-[#111111] border-[#222222]">
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Method Selection */}
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Payout Method</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'bank_transfer', label: 'Bank Transfer', icon: <Bank className="w-5 h-5" /> },
                    { id: 'paypal', label: 'PayPal', icon: <PaypalLogo className="w-5 h-5" /> },
                    { id: 'cashapp', label: 'Cash App', icon: <CurrencyDollar className="w-5 h-5" /> },
                  ].map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMethod(m.id)}
                      data-testid={`method-${m.id}`}
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-sm font-medium transition-all ${
                        method === m.id
                          ? 'bg-[#22C55E]/10 border-[#22C55E] text-[#22C55E]'
                          : 'bg-[#0A0A0A] border-[#333333] text-[#999999] hover:border-[#555555]'
                      }`}
                    >
                      {m.icon}
                      {m.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#999999]">Withdrawals are typically processed within 1-3 business days.</p>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Amount</Label>
                <div className="flex items-center rounded-sm border bg-[#0A0A0A] border-[#333333]">
                  <span className="pl-3 pr-1 text-lg font-bold text-[#22C55E]">$</span>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="border-0 bg-transparent text-lg font-semibold text-white focus-visible:ring-0"
                    data-testid="withdraw-amount-input"
                    required
                  />
                </div>
                {amount && parseFloat(amount) > 0 && (
                  <p className="text-xs text-[#999999]">
                    Balance after withdrawal: ${(balance - parseFloat(amount || 0)).toFixed(2)}
                  </p>
                )}
              </div>

              {/* Bank Transfer Fields */}
              {method === 'bank_transfer' && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Bank Name</Label>
                    <div className="flex gap-2">
                      <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g., Chase, Bank of America" className="rounded-sm flex-1 bg-[#0A0A0A] border-[#333333] text-white" data-testid="bank-name-input" required />
                      <VoiceInputButton onTranscript={(text) => setBankName(prev => prev ? prev + ' ' + text : text)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Account Holder Name</Label>
                    <div className="flex gap-2">
                      <Input value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} placeholder="Full name on account" className="rounded-sm flex-1 bg-[#0A0A0A] border-[#333333] text-white" data-testid="account-holder-input" required />
                      <VoiceInputButton onTranscript={(text) => setAccountHolder(prev => prev ? prev + ' ' + text : text)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Routing Number</Label>
                      <Input value={routingNumber} onChange={(e) => setRoutingNumber(e.target.value)} placeholder="9 digits" className="rounded-sm bg-[#0A0A0A] border-[#333333] text-white" data-testid="routing-number-input" required />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Account Number</Label>
                      <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="Account number" className="rounded-sm bg-[#0A0A0A] border-[#333333] text-white" data-testid="account-number-input" required />
                    </div>
                  </div>
                </div>
              )}

              {/* PayPal Fields */}
              {method === 'paypal' && (
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">PayPal Email</Label>
                  <Input type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} placeholder="your@paypal.com" className="rounded-sm bg-[#0A0A0A] border-[#333333] text-white" data-testid="paypal-email-input" required />
                </div>
              )}

              {/* Cash App Fields */}
              {method === 'cashapp' && (
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Cash App Tag</Label>
                  <Input value={cashappTag} onChange={(e) => setCashappTag(e.target.value)} placeholder="$YourCashTag" className="rounded-sm bg-[#0A0A0A] border-[#333333] text-white" data-testid="cashapp-tag-input" required />
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold text-[#999999]">Notes (Optional)</Label>
                <div className="relative">
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional info..." rows={2} className="rounded-sm resize-none bg-[#0A0A0A] border-[#333333] text-white" data-testid="withdraw-notes" />
                  <div className="absolute bottom-2 right-2">
                    <VoiceInputButton onTranscript={(text) => setNotes(prev => prev ? prev + ' ' + text : text)} />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || hasPending || !amount}
                className="w-full bg-[#22C55E] hover:bg-[#1EA34B] text-black font-bold h-12 rounded-lg text-base"
                data-testid="submit-withdrawal"
              >
                {loading ? 'Processing...' : `Request Withdrawal $${parseFloat(amount || 0).toFixed(2)}`}
              </Button>
              <p className="text-xs text-center text-[#999999]">
                Withdrawals are reviewed and processed within 1-3 business days.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Withdrawal History */}
        {withdrawals.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[#999999]">Withdrawal History</h2>
            {withdrawals.map(w => (
              <Card key={w.withdrawal_id} className="bg-[#111111] border-[#222222]" data-testid={`withdrawal-${w.withdrawal_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-white">${w.amount.toFixed(2)}</p>
                      <p className="text-xs text-[#999999]">{methodLabel(w.method)} &middot; {new Date(w.created_at).toLocaleDateString()}</p>
                      {w.admin_note && <p className="text-xs text-[#666666] mt-1">Note: {w.admin_note}</p>}
                    </div>
                    {statusBadge(w.status)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
